import { useMemo, useState } from 'react';
import { CreateWorkflowNode, DataLoaderArchiveFileConfiguration, ExtractorSettings } from 'api/model';
import MultiBooleanToggleField from 'components/form/MultiBooleanToggleField';
import { DataArray, TextFields } from '@mui/icons-material';
import { useNodeValidation } from 'hooks/useNodeValidation';
import FormBlock from 'components/form/FormBlock';
import { NodeFormProps } from 'api/workflows-steps/formSchema';

export const EXTRACTOR_SETTINGS_SOFTWARE_PROJECT = {
  'ui:field': MultiBooleanToggleField,
  'ui:options': {
    fields: [
      {
        name: 'include_text_content',
        title: 'Text Content',
        icon: <TextFields />,
        description: 'If enabled, the decoded text content of the files will be included (text_content column)',
      },
      {
        name: 'include_binary_content',
        title: 'Binary Content',
        icon: <DataArray />,
        description: 'If enabled, the binary content of the files will be included in base64 (binary_content column)',
      },
    ],
  },
};

const ArchiveFileForm = ({ initialValues = {} as CreateWorkflowNode, ...props }: NodeFormProps) => {
  const [formData, setFormData] = useState<CreateWorkflowNode>(initialValues);

  const { validate } = useNodeValidation(initialValues.type);

  const uiSchema = useMemo(() => {
    const schema = { ...props.uiSchema };
    const configuration = formData.configuration as DataLoaderArchiveFileConfiguration;
    if (configuration?.extractor_type === 'software_project_extractor') {
      schema.configuration.extractor_settings = EXTRACTOR_SETTINGS_SOFTWARE_PROJECT;
    } else {
      delete schema.configuration.extractor_settings;
    }
    return schema;
  }, [props.uiSchema, formData]);

  const handleOnChange = (newFormData: CreateWorkflowNode) => {
    const newConfiguration = newFormData.configuration as DataLoaderArchiveFileConfiguration;
    const oldConfiguration = formData.configuration as DataLoaderArchiveFileConfiguration;
    if (newConfiguration?.extractor_type !== oldConfiguration?.extractor_type) {
      const oldSettingsKeys = Object.keys(oldConfiguration?.extractor_settings || {});
      const newSettingsKeys = Object.keys(newConfiguration?.extractor_settings || {});

      const addedSettings = newSettingsKeys.filter((x) => !oldSettingsKeys.includes(x));

      const settings = addedSettings.reduce<ExtractorSettings>((obj, key) => {
        (obj as any)[key] = newConfiguration?.extractor_settings?.[key as keyof ExtractorSettings];
        return obj;
      }, {});

      newConfiguration.extractor_settings = Object.keys(settings).length > 0 ? settings : undefined;
      if (newConfiguration.extractor_type === 'software_project_extractor' && !newConfiguration.extractor_settings) {
        newConfiguration.extractor_settings = {
          include_text_content: true,
          include_binary_content: false,
        };
      }
    }

    const data = { ...newFormData, configuration: newConfiguration };
    setFormData(data);
    props.onChange?.(data);
  };

  const handleSubmit = async (formData: CreateWorkflowNode) => {
    if (await validate(formData)) {
      props.onSubmit?.(formData);
    }
  };

  return (
    <FormBlock
      {...props}
      uiSchema={uiSchema}
      onSubmit={handleSubmit}
      onChange={handleOnChange}
      initialValues={initialValues}
    />
  );
};

export default ArchiveFileForm;
